import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { EmptySearchResult } from '@/components'
import { triggerToast } from '@/components/Toast'
import { Spinner } from '@/components/ui/spinner'
import HistoryAuditRow from '@/features/audit/components/HistoryAuditRow'
import HistoryFiltersBar from '@/features/audit/components/HistoryFiltersBar'
import {
  getCreatedAtFilter,
  useExportHistory,
  useHistoryPageList,
} from '@/features/audit/hooks'
import { HistoryFilters, HistoryTimeFrame } from '@/features/audit/types'
import { useInfinityLoadMore } from '@/features/user-management/hooks/useInfinityLoadMore'
import { useMinute } from '@/hooks/useMinute'
import EmptyHistoryImage from '@/public/images/empty-history.svg'
import { AuditType, RequestAuditLog } from '@/schema'

import ExportHistoryDialog from '../../features/audit/components/ExportHistoryDialog'
import { groupByDate } from '../../features/audit/utils'
import { SYSTEM_USER_ID, TABLE_PAGE_SIZE } from '../../utils/constants'

const PAGE_SIZE = TABLE_PAGE_SIZE

const hasActiveFilters = (f: HistoryFilters): boolean =>
  !!(
    (f.user_id && f.user_id.length > 0) ||
    f.timeFrame ||
    (f.logTypes && f.logTypes.length > 0)
  )

// Loading skeleton
const SkeletonRow: React.FC = () => (
  <div className="flex gap-3 py-3 px-1 animate-pulse">
    <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
    <div className="flex-1 space-y-2 pt-1">
      <div className="h-3 bg-gray-200 rounded w-28" />
      <div className="h-3 bg-gray-200 rounded w-52" />
    </div>
    <div className="h-3 bg-gray-200 rounded w-12 flex-shrink-0 mt-1" />
  </div>
)

// ---------------------------------------------------------------------------
// Main Page Form
// ---------------------------------------------------------------------------

const SKELETON_KEYS = Array.from({ length: 8 }, () => crypto.randomUUID())

export const HistoryListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const readFiltersFromURL = (): HistoryFilters => ({
    user_id: searchParams.getAll('user_id'),
    timeFrame: (searchParams.get('time_frame') as HistoryTimeFrame) || null,
    startDate: searchParams.get('start_date') || null,
    endDate: searchParams.get('end_date') || null,
    logTypes: searchParams.getAll('log_type'),
  })

  const [filters, setFilters] = useState<HistoryFilters>(() =>
    readFiltersFromURL()
  )

  const pushToURL = useCallback(
    (f: HistoryFilters) => {
      const p = new URLSearchParams()
      if (f.user_id?.length) f.user_id.forEach((v) => p.append('user_id', v))
      if (f.timeFrame) p.set('time_frame', f.timeFrame)
      if (f.startDate) p.set('start_date', f.startDate)
      if (f.endDate) p.set('end_date', f.endDate)
      if (f.logTypes?.length) f.logTypes.forEach((v) => p.append('log_type', v))
      setSearchParams(p, { replace: true })
    },
    [setSearchParams]
  )

  const handleFiltersChange = useCallback(
    (next: Partial<HistoryFilters>) => {
      setFilters((prev) => {
        const merged = { ...prev, ...next }
        pushToURL(merged)
        return merged
      })
    },
    [pushToURL]
  )

  // Refresh relative timestamps every 30s
  useMinute(30000)

  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const exportMutation = useExportHistory()

  const handleExportClick = useCallback(
    (timestamp: string) => {
      const filterBy: NonNullable<RequestAuditLog['filter_by']> = {}

    if (filters.logTypes && filters.logTypes.length > 0) {
      filterBy.log_type = filters.logTypes
    }
    if (filters.user_id && filters.user_id.length > 0) {
      filterBy.user_id = filters.user_id
    }

    const createdAtFilter = getCreatedAtFilter(
      filters.timeFrame,
      filters.startDate,
      filters.endDate
    )
    if (createdAtFilter) {
      filterBy.created_at = createdAtFilter
    }
 
    const payload: RequestAuditLog = {
      audit_type: AuditType.HISTORY_LOG,
      limit: 20,
      offset: 20,      
      timestamp,
      filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
    }

    exportMutation.mutate(payload, {
      onSuccess: () => {
        setExportDialogOpen(true)
      },
      onError: () => {
        triggerToast('error', 'Failed to export history. Please try again.')
      },
    })
  }, [filters, exportMutation])

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useHistoryPageList({
    ...filters,
    limit: PAGE_SIZE,
  })

  const { loadMoreRef, scrollContainerRef } = useInfinityLoadMore({
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
  })

  useEffect(() => {
    if (isError) {
      triggerToast('error', 'Failed to load history. Please try again.')
    }
  }, [isError])

  const items = useMemo(() => {
    if (!data?.pages) return []
    return data.pages
      .flatMap((page) => page.data || [])
      .filter((item) => item.user_id !== SYSTEM_USER_ID)
  }, [data?.pages])

  const groups = useMemo(() => groupByDate(items, 'Today'), [items])

  const isEmpty = !isLoading && items.length === 0
  const isFiltered = hasActiveFilters(filters)

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-light-200 px-6 divide-y divide-gray-light-100">
          {SKELETON_KEYS.map((key) => (
            <SkeletonRow key={key} />
          ))}
        </div>
      )
    }

    if (isEmpty) {
      if (isFiltered) {
        return (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <EmptySearchResult
              translationKey="history"
              className="flex flex-col items-center justify-center text-center"
            />
          </div>
        )
      }
      return (
        <div className="flex flex-col items-center justify-center py-24 gap-1">
          <img
            src={EmptyHistoryImage}
            alt="No history"
            className="w-40 h-40 object-contain pb-6"
          />
          <p className="text-md font-semibold text-primary-900 m-0">
            No History Available
          </p>
          <p className="text-sm text-gray-light-600 m-0 text-center">
            <p className="m-0">There's no activity history to display yet.</p>
            <p className="m-0">
              Once actions are taken, they will appear here.
            </p>
          </p>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-4 max-w-[800px] mx-auto w-full">
        {groups.map((group) => (
          <div
            key={group.key}
            className="bg-white rounded-xl shadow-sm border border-gray-light-200 overflow-hidden"
          >
            {/* Date label */}
            <div className="px-6 py-3">
              <span className="text-lg font-semibold text-primary-900">
                {group.label}
              </span>
            </div>

            {/* Rows */}
            <div className="px-5 divide-y divide-gray-light-100">
              {group.entries.map((entry) => (
                <HistoryAuditRow
                  key={entry.id}
                  entry={entry}
                  isToday={group.isToday}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Loading more indicator — shown while fetching next page */}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-4">
            <Spinner className="size-5 text-brand-600" />
          </div>
        )}

        {/* Invisible element for IntersectionObserver */}
        <div ref={loadMoreRef} className="h-1" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex-shrink-0">
        <h1 className="m-0 font-semibold text-display-xs text-primary-900">
          History
        </h1>
      </div>

      <div className="flex-shrink-0">
        <HistoryFiltersBar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onExportClick={handleExportClick}
          isExporting={exportMutation.isPending}
        />
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto bg-secondary -mx-8 -mb-8 pt-6"
      >
        {renderContent()}
      </div>

      <ExportHistoryDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
      />
    </div>
  )
}

export default HistoryListPage
